import { useState, useEffect, useCallback } from 'react';
import { CaretLeftIcon, TrendUpIcon, TrendDownIcon, WarningCircleIcon, CaretDownIcon } from '@phosphor-icons/react';

import { useRouter } from '@/lib/router';
import Image from '@/components/ui/Image';
import { useFormatCurrency } from '@/hooks/useFormatCurrency';
import { useFinance } from '@/hooks/useFinance';
import {
  TRANSACTION_TYPE_LABELS,
  appendInvestmentDetailPage,
  assetTagToneCls,
  formatDate,
  formatPercent,
  getNonZeroExpenses,
  getTypeBadgeClass,
} from '@/lib/investments/transactions';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { InvestmentAvailabilityNotice } from '@/components/InvestmentAvailabilityNotice';
import { InvestmentStatusBadge, InvestmentWithdrawnNotice } from '@/components/InvestmentStatusBadge';
import type { AllocationBreakdown, InvestmentDetail, InvestmentTransaction } from '@/types';

const TYPE_TO_ALLOCATION: Record<string, keyof AllocationBreakdown> = {
  'Renda Fixa': 'fixedIncome',
  'Renda Variável': 'variableIncome',
  'Fundos': 'funds',
  'Imóveis': 'realEstate',
  'FII': 'realEstate',
};

const TRANSACTIONS_PAGE_SIZE = 50;

interface Props {
  investmentId: string;
}

function DetailTransactionRow({
  tx,
  isExpanded,
  onToggle,
}: {
  tx: InvestmentTransaction;
  isExpanded: boolean;
  onToggle: (id: string) => void;
}) {
  // Hook in the leaf, not a threaded prop — keeps privacy-awareness of money
  // strings unforgeable by callers (same pattern as InvestmentAllocationChart).
  const formatCurrency = useFormatCurrency();
  const hasExpenses = tx.expenses && getNonZeroExpenses(tx.expenses).length > 0;
  const hasExtra = Boolean(tx.brokerageNumber || tx.agreedRate != null || tx.description || hasExpenses);

  function handleToggle() {
    onToggle(tx.id);
  }

  return (
    <button
      type="button"
      disabled={!hasExtra}
      aria-expanded={hasExtra ? isExpanded : undefined}
      className={`grid w-full grid-cols-[minmax(6rem,0.8fr)_minmax(8rem,1fr)_minmax(7rem,1fr)_minmax(7rem,1fr)_2rem] items-center gap-x-3 px-4 py-4 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring disabled:cursor-default @4xl:grid-cols-[minmax(6rem,0.8fr)_minmax(6rem,0.8fr)_minmax(8rem,1fr)_minmax(7rem,1fr)_minmax(7rem,1fr)_minmax(5rem,0.7fr)_minmax(7rem,1fr)_2rem] @5xl:px-6 ${hasExtra ? 'cursor-pointer' : ''} ${isExpanded ? 'bg-surface-sunken/60' : hasExtra ? 'hover:bg-accent/40' : ''}`}
      onClick={handleToggle}
    >
      <span className="min-w-0 truncate text-sm text-text-secondary">{formatDate(tx.date)}</span>
      <span className="hidden min-w-0 truncate text-sm text-text-tertiary @4xl:block">
        {tx.tradeDate ? formatDate(tx.tradeDate) : '—'}
      </span>
      <span className="min-w-0">
        <span className={`inline-flex max-w-full truncate rounded px-2 py-0.5 text-2xs font-bold uppercase ${getTypeBadgeClass(tx.type)}`}>
          {TRANSACTION_TYPE_LABELS[tx.type] ?? tx.type}
        </span>
      </span>
      <span className="min-w-0 truncate text-right font-mono text-sm font-bold text-text-primary tabular-nums">
        {formatCurrency(tx.value)}
      </span>
      <span className="min-w-0 truncate text-right font-mono text-sm text-text-secondary tabular-nums">
        {tx.netValue != null ? formatCurrency(tx.netValue) : '—'}
      </span>
      <span className="hidden min-w-0 truncate text-right font-mono text-sm text-text-secondary tabular-nums @4xl:block">
        {tx.quantity != null ? tx.quantity.toLocaleString('pt-BR', { maximumFractionDigits: 6 }) : '—'}
      </span>
      <span className="hidden min-w-0 truncate text-right font-mono text-sm text-text-secondary tabular-nums @4xl:block">
        {tx.unitPrice != null ? formatCurrency(tx.unitPrice) : '—'}
      </span>
      <span className="flex justify-end">
        {hasExtra && (
          <CaretDownIcon size={14} className={`text-text-tertiary transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
        )}
      </span>
    </button>
  );
}

function MobileTransactionCard({
  tx,
  isExpanded,
  onToggle,
}: {
  tx: InvestmentTransaction;
  isExpanded: boolean;
  onToggle: (id: string) => void;
}) {
  const formatCurrency = useFormatCurrency();
  const hasExpenses = tx.expenses && getNonZeroExpenses(tx.expenses).length > 0;
  // Only detail fields gate the toggle — the description is already fully shown
  // in the header, so a description-only tx would expand to an empty section.
  const hasExtra = Boolean(tx.tradeDate || tx.quantity != null || tx.unitPrice != null || tx.brokerageNumber || tx.agreedRate != null || hasExpenses);
  const expenses = tx.expenses ? getNonZeroExpenses(tx.expenses) : [];

  function handleToggleDetails() {
    onToggle(tx.id);
  }

  return (
    <article className="rounded-2xl border border-border-subtle bg-surface p-4 shadow-none">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <Badge className={getTypeBadgeClass(tx.type)} size="sm">
              {TRANSACTION_TYPE_LABELS[tx.type] ?? tx.type}
            </Badge>
            <span className="text-sm font-medium text-text-secondary">{formatDate(tx.date)}</span>
          </div>
          {tx.description && (
            <p className="mt-2 line-clamp-2 text-sm/snug text-text-secondary">{tx.description}</p>
          )}
        </div>
        <div className="max-w-1/2 shrink-0 text-right">
          <p className="font-mono text-base font-bold wrap-break-word text-text-primary tabular-nums">{formatCurrency(tx.value)}</p>
          {tx.netValue != null && tx.netValue !== tx.value && (
            <p className="font-mono text-2xs wrap-break-word text-text-tertiary tabular-nums">{formatCurrency(tx.netValue)} líq.</p>
          )}
        </div>
      </div>

      {(tx.quantity != null || tx.unitPrice != null || tx.tradeDate) && (
        <div className="mt-3 grid grid-cols-1 gap-2 rounded-xl bg-surface-sunken p-3 text-base sm:text-sm @sm:grid-cols-2">
          {tx.quantity != null && (
            <div className="min-w-0">
              <span className="block text-2xs font-semibold tracking-wide text-text-tertiary uppercase">Qtd</span>
              <span className="font-mono font-bold wrap-break-word text-text-primary tabular-nums">{tx.quantity.toLocaleString('pt-BR', { maximumFractionDigits: 6 })}</span>
            </div>
          )}
          {tx.unitPrice != null && (
            <div className="min-w-0 text-right">
              <span className="block text-2xs font-semibold tracking-wide text-text-tertiary uppercase">Unit.</span>
              <span className="font-mono font-bold wrap-break-word text-text-primary tabular-nums">{formatCurrency(tx.unitPrice)}</span>
            </div>
          )}
          {tx.tradeDate && (
            <div className="min-w-0 @sm:col-span-2">
              <span className="block text-2xs font-semibold tracking-wide text-text-tertiary uppercase">Liquidação</span>
              <span className="text-text-primary">{formatDate(tx.tradeDate)}</span>
            </div>
          )}
        </div>
      )}

      {hasExtra && (
        <button
          type="button"
          aria-expanded={isExpanded}
          onClick={handleToggleDetails}
          className="mt-3 flex min-h-10 w-full items-center justify-between gap-3 rounded-xl bg-surface-sunken px-3 text-left active:bg-accent"
        >
          <span className="text-2xs font-semibold tracking-wide text-text-tertiary uppercase">
            {isExpanded ? 'Ocultar detalhes' : 'Ver detalhes'}
          </span>
          <CaretDownIcon size={14} className={`text-text-tertiary transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
        </button>
      )}

      {isExpanded && (
        <div className="mt-3 grid grid-cols-1 gap-3 border-t border-border-subtle pt-3 text-base sm:text-sm @sm:grid-cols-2">
          {tx.tradeDate && (
            <div className="min-w-0">
              <span className="block text-2xs font-medium text-text-tertiary">Liquidação</span>
              <span className="text-text-primary">{formatDate(tx.tradeDate)}</span>
            </div>
          )}
          {tx.quantity != null && (
            <div className="min-w-0">
              <span className="block text-2xs font-medium text-text-tertiary">Quantidade</span>
              <span className="font-mono wrap-break-word text-text-primary tabular-nums">{tx.quantity.toLocaleString('pt-BR', { maximumFractionDigits: 6 })}</span>
            </div>
          )}
          {tx.unitPrice != null && (
            <div className="min-w-0">
              <span className="block text-2xs font-medium text-text-tertiary">Preço unitário</span>
              <span className="font-mono wrap-break-word text-text-primary tabular-nums">{formatCurrency(tx.unitPrice)}</span>
            </div>
          )}
          {tx.brokerageNumber && (
            <div className="min-w-0">
              <span className="block text-2xs font-medium text-text-tertiary">Nota</span>
              <span className="font-mono wrap-break-word text-text-primary">{tx.brokerageNumber}</span>
            </div>
          )}
          {tx.agreedRate != null && (
            <div className="min-w-0">
              <span className="block text-2xs font-medium text-text-tertiary">Taxa acordada</span>
              <span className="font-mono wrap-break-word text-text-primary tabular-nums">{tx.agreedRate.toFixed(4)}%</span>
            </div>
          )}
          {expenses.length > 0 && (
            <div className="@sm:col-span-2">
              <span className="mb-1 block text-2xs font-medium text-text-tertiary">Custos</span>
              <div className="flex flex-wrap gap-x-3 gap-y-1">
                {expenses.map((e) => (
                  <span key={e.label} className="text-text-primary">
                    {e.label}: <span className="font-mono tabular-nums">{formatCurrency(e.value)}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </article>
  );
}

export function InvestmentDetailView({ investmentId }: Props) {
  const router = useRouter();
  const formatCurrency = useFormatCurrency();
  const { fetchInvestmentDetail } = useFinance();

  const [data, setData] = useState<InvestmentDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedTx, setExpandedTx] = useState<string | null>(null);

  useEffect(() => {
    setIsLoading(true);
    fetchInvestmentDetail(investmentId, { limit: TRANSACTIONS_PAGE_SIZE, offset: 0 })
      .then((result) => {
        if (result) setData(result);
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Erro ao carregar investimento');
      })
      .finally(() => setIsLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [investmentId]);

  const loadMoreTransactions = async () => {
    const nextOffset = data?.transactions.nextOffset;
    if (!data?.transactions.hasMore || nextOffset == null) return;
    setIsLoadingMore(true);
    try {
      const next = await fetchInvestmentDetail(investmentId, {
        limit: TRANSACTIONS_PAGE_SIZE,
        offset: nextOffset,
      });
      setData((current) => appendInvestmentDetailPage(current, next));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar movimentações');
    } finally {
      setIsLoadingMore(false);
    }
  };

  const handleBack = useCallback(() => {
    router.push('/investments');
  }, [router]);

  const handleToggleTransaction = useCallback((id: string) => {
    setExpandedTx((current) => (current === id ? null : id));
  }, []);

  const inv = data?.investment;
  const allocationKey = inv
    ? (inv.subtype === 'FII' ? 'realEstate' : (TYPE_TO_ALLOCATION[inv.type] ?? 'other'))
    : 'other';

  return (
    <div className="space-y-6 pb-8">
      <Button
        variant="ghost"
        size="sm"
        onClick={handleBack}
        className="-ml-2 text-base text-text-secondary hover:text-text-primary sm:text-sm"
      >
        <CaretLeftIcon size={16} />
        Investimentos
      </Button>

      {error && (
        <Card className="border-error-border bg-error-surface">
          <CardContent className="flex items-center gap-3 py-4">
            <WarningCircleIcon size={16} className="shrink-0 text-error" />
            <p className="text-base text-error-text sm:text-sm">{error}</p>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="pt-4 sm:pt-6">
          {isLoading ? (
            <div className="space-y-3">
              <div className="h-4 w-32 animate-pulse rounded bg-muted" />
              <div className="h-6 w-2/3 animate-pulse rounded bg-muted" />
              <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="space-y-1">
                    <div className="h-3 w-16 animate-pulse rounded bg-muted" />
                    <div className="h-5 w-24 animate-pulse rounded bg-muted" />
                  </div>
                ))}
              </div>
            </div>
          ) : inv ? (
            <>
              <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-start">
                <div className="min-w-0 flex-1">
                  <div className="mb-1 flex min-w-0 items-center gap-2">
                    {inv.institutionImage && (
                      <Image src={inv.institutionImage} alt="" width={16} height={16} className="shrink-0 rounded" />
                    )}
                    <span className="min-w-0 truncate text-sm text-text-secondary sm:text-xs">{inv.institution || inv.issuer || '—'}</span>
                  </div>
                  <h1 className="text-lg/tight font-bold wrap-break-word text-text-primary sm:text-xl">{inv.name}</h1>
                  {inv.issuer && inv.institution && (
                    <p className="mt-1 text-sm wrap-break-word text-text-tertiary sm:text-xs">{inv.issuer}</p>
                  )}
                </div>
                <span className="flex shrink-0 flex-wrap items-center gap-1.5">
                  <span className={`inline-flex w-fit max-w-full rounded-md px-2 py-1 font-mono text-3xs font-semibold tracking-wider uppercase ${assetTagToneCls(allocationKey)}`}>
                    {inv.subtype || inv.type}
                  </span>
                  <InvestmentStatusBadge status={inv.status} />
                </span>
              </div>

              <div className="mb-4 space-y-2 empty:hidden">
                <InvestmentWithdrawnNotice status={inv.status} />
                <InvestmentAvailabilityNotice inv={inv} />
              </div>

              <div className="@container border-t border-border-subtle pt-4">
                <div className="grid grid-cols-1 gap-3 @sm:grid-cols-2 @3xl:grid-cols-4">
                <div className="rounded-xl bg-surface-sunken p-3 sm:bg-transparent sm:p-0">
                  <p className="mb-0.5 font-mono text-3xs tracking-widest text-text-tertiary uppercase">Saldo Atual</p>
                  <p className="font-mono text-base font-bold wrap-break-word text-text-primary tabular-nums sm:text-sm">{formatCurrency(inv.balance)}</p>
                </div>
                <div className="rounded-xl bg-surface-sunken p-3 sm:bg-transparent sm:p-0">
                  <p className="mb-0.5 font-mono text-3xs tracking-widest text-text-tertiary uppercase">Valor Investido</p>
                  <p className="font-mono text-base font-bold wrap-break-word text-text-primary tabular-nums sm:text-sm">
                    {inv.amountOriginal != null ? formatCurrency(inv.amountOriginal) : '—'}
                  </p>
                </div>
                <div className="rounded-xl bg-surface-sunken p-3 sm:bg-transparent sm:p-0">
                  <p className="mb-0.5 font-mono text-3xs tracking-widest text-text-tertiary uppercase">Lucro/Prejuízo</p>
                  {inv.profit != null ? (
                    <div className="flex min-w-0 items-center gap-1">
                      {inv.profit >= 0
                        ? <TrendUpIcon size={12} className="text-amount-positive" />
                        : <TrendDownIcon size={12} className="text-amount-negative" />}
                      <p className={`break-words text-base sm:text-sm font-bold font-mono tabular-nums ${inv.profit >= 0 ? 'text-amount-positive' : 'text-amount-negative'}`}>
                        {formatCurrency(inv.profit)}
                      </p>
                    </div>
                  ) : (
                    <p className="text-base font-bold text-text-tertiary sm:text-sm">—</p>
                  )}
                  {inv.profitPercent != null && (
                    <p className={`font-mono text-2xs font-bold tabular-nums ${inv.profitPercent >= 0 ? 'text-amount-positive' : 'text-amount-negative'}`}>
                      {formatPercent(inv.profitPercent)}
                    </p>
                  )}
                </div>
                <div className="rounded-xl bg-surface-sunken p-3 sm:bg-transparent sm:p-0">
                  <p className="mb-0.5 font-mono text-3xs tracking-widest text-text-tertiary uppercase">Rentab. Anual</p>
                  <p className="font-mono text-base font-bold wrap-break-word text-text-primary tabular-nums sm:text-sm">
                    {inv.annualRate != null ? `${inv.annualRate.toFixed(2)}%` : inv.rate || '—'}
                  </p>
                </div>
                </div>
              </div>
            </>
          ) : null}
        </CardContent>
      </Card>

      <Card className="overflow-hidden">
        <div className="border-b border-border-subtle p-4 sm:px-6">
          <h2 className="text-sm font-semibold text-text-primary">
            Movimentações
            {data && data.transactions.items.length > 0 && (
              <span className="ml-2 font-normal text-text-tertiary">
                ({data.transactions.items.length}{data.transactions.total > data.transactions.items.length ? ` de ${data.transactions.total}` : ''})
              </span>
            )}
          </h2>
        </div>

        {isLoading ? (
          <div className="divide-y divide-border-subtle">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex items-center gap-4 p-4 sm:px-6">
                <div className="h-4 w-24 animate-pulse rounded bg-muted" />
                <div className="h-4 w-16 animate-pulse rounded bg-muted" />
                <div className="ml-auto h-4 w-28 animate-pulse rounded bg-muted" />
              </div>
            ))}
          </div>
        ) : !data || data.transactions.items.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <p className="text-base text-text-secondary sm:text-sm">Nenhuma movimentação encontrada</p>
          </div>
        ) : (
          <>
            {/* Desktop adaptive grid */}
            <div className="@container hidden md:block">
              <div className="grid grid-cols-[minmax(6rem,0.8fr)_minmax(8rem,1fr)_minmax(7rem,1fr)_minmax(7rem,1fr)_2rem] gap-x-3 border-b border-border-subtle px-4 py-3 text-2xs font-bold text-text-tertiary @4xl:grid-cols-[minmax(6rem,0.8fr)_minmax(6rem,0.8fr)_minmax(8rem,1fr)_minmax(7rem,1fr)_minmax(7rem,1fr)_minmax(5rem,0.7fr)_minmax(7rem,1fr)_2rem] @5xl:px-6">
                <span>Data op.</span>
                <span className="hidden @4xl:block">Liquidação</span>
                <span>Tipo</span>
                <span className="text-right">Valor bruto</span>
                <span className="text-right">Valor líq.</span>
                <span className="hidden text-right @4xl:block">Qtd</span>
                <span className="hidden text-right @4xl:block">Preço unit.</span>
                <span className="sr-only">Info</span>
              </div>

              <div className="divide-y divide-border-subtle">
                {data.transactions.items.map((tx) => (
                  <DetailTransactionRow
                    key={tx.id}
                    tx={tx}
                    isExpanded={expandedTx === tx.id}
                    onToggle={handleToggleTransaction}
                  />
                ))}
              </div>
            </div>

            {expandedTx && (
              <div className="hidden border-t border-border-subtle bg-surface-sunken/40 p-4 md:block lg:px-6">
                {(() => {
                  const tx = data.transactions.items.find((t) => t.id === expandedTx);
                  if (!tx) return null;
                  const expenses = tx.expenses ? getNonZeroExpenses(tx.expenses) : [];
                  return (
                    <div className="@container grid grid-cols-1 gap-4 text-base sm:text-sm @lg:grid-cols-2 @3xl:grid-cols-4">
                      {tx.description && (
                        <div className="@lg:col-span-2 @3xl:col-span-4">
                          <span className="text-text-tertiary">Descrição: </span>
                          <span className="text-text-primary">{tx.description}</span>
                        </div>
                      )}
                      {tx.brokerageNumber && (
                        <div>
                          <span className="text-text-tertiary">Nota: </span>
                          <span className="font-mono text-text-primary">{tx.brokerageNumber}</span>
                        </div>
                      )}
                      {tx.agreedRate != null && (
                        <div>
                          <span className="text-text-tertiary">Taxa Acordada: </span>
                          <span className="font-mono text-text-primary">{tx.agreedRate.toFixed(4)}%</span>
                        </div>
                      )}
                      {expenses.length > 0 && (
                        <div className="@lg:col-span-2 @3xl:col-span-4">
                          <span className="mb-1 block text-text-tertiary">Custos:</span>
                          <div className="flex flex-wrap gap-x-4 gap-y-1">
                            {expenses.map((e) => (
                              <span key={e.label} className="text-text-secondary">
                                {e.label}: <span className="font-mono">{formatCurrency(e.value)}</span>
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Mobile card list */}
            <div className="@container bg-surface-sunken/70 p-3 md:hidden">
              <div className="grid grid-cols-1 gap-3 @2xl:grid-cols-2">
              {data.transactions.items.map((tx) => (
                <MobileTransactionCard
                  key={tx.id}
                  tx={tx}
                  isExpanded={expandedTx === tx.id}
                  onToggle={handleToggleTransaction}
                />
              ))}
              </div>
            </div>
            {data.transactions.hasMore && (
              <div className="border-t border-border-subtle p-3">
                <Button
                  variant="ghost"
                  onClick={loadMoreTransactions}
                  disabled={isLoadingMore}
                  className="h-11 w-full bg-surface-sunken text-text-primary hover:bg-accent"
                >
                  {isLoadingMore ? 'Carregando...' : 'Carregar mais'}
                </Button>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}
